import { FC, useContext, useMemo, useCallback, memo } from 'react';
import { TaskAddEditContext } from '../../../../contexts/TaslAddEditFormContext';
import { RESPONSIBLE_PLACEHOLDER, TASK_DESC_LIMIT, TASK_NAME_LIMIT } from '../FormConstants/FormConstants';
import Button from '../../../UiLib/Button/Button';

import OldViewButton from "../../../../assets/editElementGray.svg";
import ArrowDown from "../../../../assets/arrowDown.svg";
import ExitCross from "../../../../assets/cancelGreey.svg";

import TextEditor from '../../../Shared/TextEditor/TextEditor';
import TasksTagDisplay from '../../../Shared/TagSelectorAndDisplay/TasksTagDisplay';
import PersonLineDisplay from '../../../Shared/PersonLineDisplay';
import { fullNameFormat, initials } from '../../../../helpers/Inicials';
import PersonSelector from '../../../Shared/PersonSelector/PersonSelector';
import { Context } from '../../../..';
import { AppContext } from '../../../../App';

const MainFormTab: FC = () => {
    const taskAddEditFormContext = useContext(TaskAddEditContext);
    const { store } = useContext(Context);
    const { showToast } = useContext(AppContext);

    const taskIdMode = useMemo(() =>
        taskAddEditFormContext.taskId ? taskAddEditFormContext.taskId : undefined,
        [taskAddEditFormContext.taskId]
    );

    const truncatedTaskName = useMemo(() =>
        taskAddEditFormContext.taskName?.length > TASK_NAME_LIMIT
            ? taskAddEditFormContext.taskName.slice(0, TASK_NAME_LIMIT)
            : taskAddEditFormContext.taskName,
        [taskAddEditFormContext.taskName]
    );

    const truncatedTaskDesc = useMemo(() =>
        taskAddEditFormContext.taskDesc,
        [taskAddEditFormContext.taskDesc]
    );

    const taskNameLength = useMemo(() =>
        truncatedTaskName?.length || 0,
        [truncatedTaskName]
    );

    const responsibleName = useMemo(() => {
        if (!taskAddEditFormContext.responsible) return RESPONSIBLE_PLACEHOLDER.name;

        return taskAddEditFormContext.responsible.name
            ? fullNameFormat(taskAddEditFormContext.responsible, "s N M")
            : initials((taskAddEditFormContext.responsible as any).responsibleName);
    }, [taskAddEditFormContext.responsible]);

    const filteredPersons = useMemo(() =>
        taskAddEditFormContext.persons
            ?.filter(xx => xx.id !== taskAddEditFormContext.responsible?.id)
            .map(person => ({
                id: person.id,
                role: person.roleName,
                name: fullNameFormat(person, "s N M"),
                surname: "",
                middleName: "",
                photoId: person.photoId,
            })) || [],
        [taskAddEditFormContext.persons, taskAddEditFormContext.responsible]
    );

    const showBoardSelection = useMemo(() =>
        !taskAddEditFormContext.columnId && !taskAddEditFormContext.taskData,
        [taskAddEditFormContext.columnId, taskAddEditFormContext.taskData]
    );

    const showOldViewButton = useMemo(() =>
        taskAddEditFormContext.mode === "edit" && taskAddEditFormContext.taskOldMarkDesc.length > 0,
        [taskAddEditFormContext.mode, taskAddEditFormContext.taskOldMarkDesc.length]
    );

    const handleSaveTaskDesc = useCallback((content: string, mention: any) => {
        // if (content === undefined || content === "<p><br></p>") {
        //     showToast("Нельзя отправить пустое сообщение!");
        //     return;
        // }
        taskAddEditFormContext.handleSaveTaskDesk(content);
    }, [taskAddEditFormContext.handleSaveTaskDesk]);

    const handleClearResponsible = useCallback((e: React.MouseEvent) => {
        taskAddEditFormContext.clearResponsible();
        e.stopPropagation();
    }, [taskAddEditFormContext.clearResponsible]);

    const handleResponsibleSelection = useCallback((person: any) => {
        taskAddEditFormContext.handleResponsibleSelection(person);
    }, [taskAddEditFormContext.handleResponsibleSelection]);

    const getTextareaClass = useMemo(() =>
        `task__create--edit__form--tab__panel--textarea ${taskAddEditFormContext.nameError ? "textarea_with_error" : ""}`,
        [taskAddEditFormContext.nameError]
    );

    const getBoardSelectClass = useMemo(() =>
        `task__create--edit__form--tab__panel--type__block--border conteiner_pointer ${taskAddEditFormContext.boardError ? "div_with_error" : ""}`,
        [taskAddEditFormContext.boardError]
    );

    const getTypeBlockClass = useMemo(() =>
        `task__create--edit__form--tab__panel--type__block--border ${taskAddEditFormContext.typeError ? "div_with_error" : ""}`,
        [taskAddEditFormContext.typeError]
    );

    const getPriorityBlockClass = useMemo(() =>
        `task__create--edit__form--tab__panel--type__block--border ${taskAddEditFormContext.priorityError ? "div_with_error" : ""}`,
        [taskAddEditFormContext.priorityError]
    );

    const getResponsibleBlockClass = useMemo(() =>
        `task__create--edit__form--tab__panel--type__block__responsible ${taskAddEditFormContext.forceLinkExecutor && "disabled"} ${taskAddEditFormContext.responsibleError ? "div_with_error" : ""}`,
        [taskAddEditFormContext.responsibleError, taskAddEditFormContext.forceLinkExecutor]
    );

    const arrowRotationStyle = useMemo(() =>
        ({ rotate: taskAddEditFormContext.openBoardsSelect ? "180deg" : "0deg" }),
        [taskAddEditFormContext.openBoardsSelect]
    );

    return (
        <div className="task__create--edit__form--tab__panel--block">
            <div className="task__create--edit__form--tab__panel--name__task">Название задачи*</div>
            <div>
                <textarea
                    disabled={taskAddEditFormContext.forceLinkName}
                    className={getTextareaClass}
                    value={truncatedTaskName}
                    placeholder="Введите название задачи"
                    cols={30}
                    rows={2}
                    onChange={taskAddEditFormContext.onChangeName}
                />
            </div>
            <div className="task__create--edit__form--tab__panel--textarea__lenght">
                {taskNameLength}/{TASK_NAME_LIMIT}
            </div>

            {/* Описание задачи */}
            <div className="task__create--edit__form--tab__panel--about__task">
                <p>Описание задачи</p>
                {showOldViewButton && (
                    <Button
                        callBack={taskAddEditFormContext.openOldView}
                        image={OldViewButton}
                        title="Открыть старое отображение"
                        titlePosition="right"
                    />
                )}
            </div>

            {taskAddEditFormContext.showWarning && (
                <div className="task__create--edit__form--tab__panel--about__task--warning">
                    <p style={{ color: "red" }}>
                        Внимание! Ввиду того что описание задачи использует устаревший формат - после сохранения
                        возможна потеря форматирования текста и картинок. Поэтому, перед сохранением, скопируйте
                        данные из старого представления (окно в левой части экрана) в окно описания задачи.
                    </p>
                </div>
            )}
            <div className={`task__create--edit__form--text-editor ${taskAddEditFormContext.descError ? "task__create--edit__form--text-editor__error" : ""}`}>
                <TextEditor
                    disabled={taskAddEditFormContext.forceLinkDescription}
                    userAcess={{
                        edit: true,
                        create: true
                    }}
                    callBackSave={handleSaveTaskDesc}
                    workMode="editor"
                    mention={{ projectId: taskAddEditFormContext.projectId ?? store.currentProjectId }}
                    editContent={truncatedTaskDesc}
                    projectId={taskAddEditFormContext.projectId ?? store.currentProjectId}
                    optionsQueryParams={taskAddEditFormContext.optionsQueryParams}
                    canSendEmptyMessage={true}
                    textLimit={(value) => taskAddEditFormContext.setMaxDescription(value)}
                    textLength={(value) => taskAddEditFormContext.setCurDescriptionLength(value)}
                />
            </div>

            {/* Доска */}
            {showBoardSelection && (
                <div>
                    <div className="task__create--edit__form--tab__panel--type__task">Доска*</div>
                    <div
                        ref={taskAddEditFormContext.buttonRef}
                        className={getBoardSelectClass}
                        onClick={() => taskAddEditFormContext.setBoardsSelect(!taskAddEditFormContext.openBoardsSelect)}
                    >
                        <div style={{ width: "100%" }}>
                            {taskAddEditFormContext.boardName?.length ? taskAddEditFormContext.boardName : "Выберите доску"}
                        </div>
                        <img src={ArrowDown} style={arrowRotationStyle} alt="Toggle board selection" />
                    </div>
                    {taskAddEditFormContext.openBoardsSelect && (
                        <div className="full-screen-overlay__board--width">
                            <div
                                ref={taskAddEditFormContext.wrapperRef}
                                className="task__create--edit__form--tab__panel--type__block--border overView task__create--edit__form--tab__panel--type__block--border__board"
                            >
                                {taskAddEditFormContext.boards?.map(board => (
                                    <div
                                        key={board.id}
                                        className="task__create--edit__form--tab__panel--tab__board"
                                        onClick={() => taskAddEditFormContext.boardIdSave(board.id, board.name)}
                                    >
                                        {board.name}
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            )}

            {/* Тип задачи */}
            <div>
                <div className="task__create--edit__form--tab__panel--type__task">Тип задачи*</div>
                <div className={getTypeBlockClass}>
                    {taskAddEditFormContext.taskTypeDisplay()}
                </div>
            </div>

            {/* Приоритет */}
            <div>
                <div className="task__create--edit__form--tab__panel--type__task">Приоритет*</div>
                <div className={getPriorityBlockClass}>
                    {taskAddEditFormContext.taskPriorityDisplay()}
                </div>
            </div>

            {/* Теги */}
            <div>
                <div className="task__create--edit__form--tab__panel--type__task">Теги</div>
                <div className="task__create--edit__form--tab__panel--type__block--border">
                    <TasksTagDisplay
                        projectId={taskAddEditFormContext.projectId ?? store.currentProjectId}
                        taskId={taskIdMode}
                        tags={taskAddEditFormContext.tags}
                        addTagsFunc={taskAddEditFormContext.onAddTags}
                        removeTags={taskAddEditFormContext.removeTags}
                        permission={taskAddEditFormContext.mode === "create" || taskAddEditFormContext.permission}
                    />
                </div>
            </div>

            {/* Исполнитель */}
            <div style={{position: "relative"}}>
                <div className="task__create--edit__form--tab__panel--type__task">
                    Исполнитель{taskAddEditFormContext.responsibleRequired ? "*" : ""}
                </div>
                {taskAddEditFormContext.forceLinkExecutor && <div className="text-editor-disabled-overlay"/>}
                <div
                    className={getResponsibleBlockClass}
                    onClick={taskAddEditFormContext.openResponsibleSelector}
                >
                    <div className="task__create--edit__form--tab__panel--block__person">
                        <PersonLineDisplay
                            name={responsibleName}
                            userId={taskAddEditFormContext.responsible?.id}
                        />
                        {taskAddEditFormContext.responsible && (
                            <div
                                className="task__create--edit__form--tab__panel--type__block--delete"
                                onClick={handleClearResponsible}
                            >
                                <img src={ExitCross} alt="Clear responsible" />
                            </div>
                        )}
                    </div>
                </div>

                {taskAddEditFormContext.showResponsibleSelector && (
                    <div style={{ position: "relative" }}>
                        <div
                            style={{ opacity: "0.5" }}
                            className="full-screen-overlay__group"
                            onClick={taskAddEditFormContext.closeResponsibleSelector}
                        />
                        <PersonSelector
                            updateFilters={() => { }}
                            loadPage={taskAddEditFormContext.appendPersons}
                            scrollEnded={false}
                            members={filteredPersons}
                            selectPerson={handleResponsibleSelection}
                            projectId={taskAddEditFormContext.projectId}
                        />
                    </div>
                )}
            </div>
        </div>
    );
};

export default memo(MainFormTab);